import { useQuery, useMutation, useQueryClient, QueryKey } from '@tanstack/react-query';
import { apiRequest } from './api';

// Cache keys
export const qk = {
  negozi: ['negozi'] as const,
  dipendentiByNeg: (neg: string) => ['dipendenti', neg] as const,
  turniNegozio: (neg: string, start: string, end: string) => ['turni', 'negozio', neg, start, end] as const,
  turniDipendente: (nome: string, start: string, end: string) => ['turni', 'dipendente', nome, start, end] as const,
  turniDipendenteByCodice: (codice: string, start: string, end: string) => ['turni', 'codice', codice, start, end] as const,
  statsNegozio: (neg: string, mese: string, anno: number) => ['stats', 'negozio', neg, mese, anno] as const,
  statsDipendenteMese: (nome: string, mese: string, anno: number) => ['stats', 'dipendente', nome, mese, anno] as const,
  dipTemp: (neg: string, mese?: string, anno?: number) => ['dip-temp', neg, mese, anno] as const,
  maternitaNegozio: (neg: string, start: string, end: string) => ['maternita', neg, start, end] as const,
  festivita: (start?: string, end?: string) => ['festivita', start, end] as const,
};

// Queries
export function useFestivita(start?: string, end?: string) {
  return useQuery({
    queryKey: qk.festivita(start, end),
    queryFn: () => {
        const params = new URLSearchParams();
        if (start) params.append('start', start);
        if (end) params.append('end', end);
        return apiRequest<any[]>(`/festivita?${params.toString()}`);
    },
    staleTime: 60 * 60 * 1000, // 1 hour
  });
}

export function useNegozi() {
  return useQuery({
    queryKey: qk.negozi,
    queryFn: () => apiRequest<any[]>('/negozi/accessibili'),
    staleTime: 5 * 60 * 1000,
  });
}

export function useDipendenti(neg?: string) {
  return useQuery({
    enabled: !!neg,
    queryKey: qk.dipendentiByNeg(neg || ''),
    queryFn: () => apiRequest<any[]>(`/dipendenti/negozio/${neg}`),
  });
}

export function useTurniNegozio(neg?: string, start?: string, end?: string, options?: { refetchInterval?: number | false }) {
  return useQuery({
    enabled: !!neg && !!start && !!end,
    queryKey: qk.turniNegozio(neg!, start!, end!),
    queryFn: () => apiRequest<any[]>(`/orari-generati/negozio/${neg}?dataInizio=${start}&dataFine=${end}`),
    refetchInterval: options?.refetchInterval ?? false,
  });
}

export function useTurniDipendente(nome?: string, start?: string, end?: string) {
  return useQuery({
    enabled: !!nome && !!start && !!end,
    queryKey: qk.turniDipendente(nome!, start!, end!),
    queryFn: () => apiRequest<any[]>(`/orari-generati/dipendente/${encodeURIComponent(nome!)}?dataInizio=${start}&dataFine=${end}`),
  });
}

/**
 * Query per ottenere i turni di un dipendente filtrando per CODICEPERSONALE (old_id).
 * Più affidabile rispetto a useTurniDipendente perché il codice personale è univoco.
 */
export function useTurniDipendenteByCodice(codicePersonale?: string, start?: string, end?: string) {
  return useQuery({
    enabled: !!codicePersonale && !!start && !!end,
    queryKey: qk.turniDipendenteByCodice(codicePersonale!, start!, end!),
    queryFn: () => apiRequest<any[]>(`/orari-generati/codice/${encodeURIComponent(codicePersonale!)}?dataInizio=${start}&dataFine=${end}`),
  });
}

export function useStatsNegozio(neg?: string, mese?: string, anno?: number) {
  return useQuery({
    enabled: !!neg && !!mese && !!anno,
    queryKey: qk.statsNegozio(neg!, mese!, anno!),
    queryFn: () => apiRequest<any[]>(`/dipendenti/statistiche?negozio=${neg}&mese=${mese}&anno=${anno}`),
  });
}

export function useStatsDipendente(nome?: string, mese?: string, anno?: number) {
  return useQuery({
    enabled: !!nome && !!mese && !!anno,
    queryKey: qk.statsDipendenteMese(nome!, mese!, anno!),
    queryFn: () => apiRequest(`/dipendenti/${encodeURIComponent(nome!)}/statistiche-mese?mese=${mese}&anno=${anno}`),
  });
}

export function useMaternitaNegozio(neg?: string, start?: string, end?: string) {
  return useQuery({
    enabled: !!neg && !!start && !!end,
    queryKey: qk.maternitaNegozio(neg!, start!, end!),
    queryFn: () => apiRequest<any[]>(`/maternita/negozio/${neg}?start=${start}&end=${end}`),
    staleTime: 5 * 60 * 1000,
  });
}

// Mutations examples
export function useGeneraOrariMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: any) => apiRequest<string>('/orari-generati/genera', {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: { 'Content-Type': 'application/json' }
    }),
    onSuccess: (_, variables) => {
      // Expect variables to contain { negozio, start, end, mese, anno }
      const { negozio, start, end, mese, anno } = variables as any;
      if (negozio && start && end) qc.invalidateQueries({ queryKey: qk.turniNegozio(negozio, start, end) as QueryKey });
      if (negozio && mese && anno) qc.invalidateQueries({ queryKey: qk.statsNegozio(negozio, mese, anno) as QueryKey });
    }
  });
}

export function useAggiornaTurnoMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number, data: any }) => apiRequest(`/orari-generati/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json' }
    }),
    onSuccess: (_, { context }: any) => {
      // context: { neg, start, end, mese, anno }
      const { neg, start, end, mese, anno } = context || {};
      if (neg && start && end) qc.invalidateQueries({ queryKey: qk.turniNegozio(neg, start, end) as QueryKey });
      if (neg && mese && anno) qc.invalidateQueries({ queryKey: qk.statsNegozio(neg, mese, anno) as QueryKey });
    }
  });
}

export function useCreaTurnoMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: any) => apiRequest('/orari-generati', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }),
    onSuccess: (_, variables: any) => {
      const { neg, start, end, mese, anno, nome, rangeStart, rangeEnd } = variables || {};
      if (neg && start && end) qc.invalidateQueries({ queryKey: qk.turniNegozio(neg, start, end) as QueryKey });
      if (nome && rangeStart && rangeEnd) qc.invalidateQueries({ queryKey: qk.turniDipendente(nome, rangeStart, rangeEnd) as QueryKey });
      if (neg && mese && anno) qc.invalidateQueries({ queryKey: qk.statsNegozio(neg, mese, anno) as QueryKey });
    }
  });
}

export function useDeleteTurnoMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiRequest(`/orari-generati/${id}`, { method: 'DELETE' }),
    onSuccess: (_, variables: any, context: any) => {
      const { neg, start, end, mese, anno, nome, rangeStart, rangeEnd } = context || {};
      if (neg && start && end) qc.invalidateQueries({ queryKey: qk.turniNegozio(neg, start, end) as QueryKey });
      if (nome && rangeStart && rangeEnd) qc.invalidateQueries({ queryKey: qk.turniDipendente(nome, rangeStart, rangeEnd) as QueryKey });
      if (neg && mese && anno) qc.invalidateQueries({ queryKey: qk.statsNegozio(neg, mese, anno) as QueryKey });
    }
  });
}

export function useScambiaTurniMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ negozio, dipendente1, dipendente2, data }: { negozio: string, dipendente1: string, dipendente2: string, data: string }) =>
      apiRequest(`/orari-generati/scambia-turni?negozio=${negozio}&dipendente1=${encodeURIComponent(dipendente1)}&dipendente2=${encodeURIComponent(dipendente2)}&data=${data}`, { method: 'POST' }),
    onSuccess: (_, { negozio, start, end, mese, anno }: any) => {
      if (negozio && start && end) qc.invalidateQueries({ queryKey: qk.turniNegozio(negozio, start, end) as QueryKey });
      if (negozio && mese && anno) qc.invalidateQueries({ queryKey: qk.statsNegozio(negozio, mese, anno) as QueryKey });
    }
  });
}
